// src/components/MarketCard.js - Updated version

import React, { useState, useEffect, useRef } from 'react';
import Tilt from 'react-parallax-tilt';
import { motion } from 'framer-motion';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';  

export default function MarketCard({ market, onBet, onSwipe }) {
  const { asset, horizon, forecast, confidence, nextSettlement } = market;
  const isUpForecast = forecast.toLowerCase() === 'up';

  const navigate = useNavigate();
  const cardRef = useRef(null);

  // e.g. "BTC/USD" → "BTC", pair "BTCUSDT", stream "btcusdt@trade"
  const symbol = asset.split('/')[0].toUpperCase();
  const pair = `${symbol}USDT`;
  const streamName = `${pair.toLowerCase()}@trade`;

  const [price, setPrice] = useState(null);
  const [priceUp, setPriceUp] = useState(true);
  const [loading, setLoading] = useState(false);
  const lastPriceRef = useRef(null);

  // Swipe detection state
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const SWIPE_THRESHOLD = 50; // Minimum distance (in px) to consider a swipe

  // Convert horizon string to minutes (e.g., "5m" -> 5)
  const getHorizonMinutes = (horizonStr) => {
    if (horizonStr.endsWith('m')) return parseInt(horizonStr);
    if (horizonStr.endsWith('h')) return parseInt(horizonStr) * 60;
    if (horizonStr.endsWith('d')) return parseInt(horizonStr) * 1440;
    return parseInt(horizonStr); // Default assume minutes
  };

  useEffect(() => {
    let ws;
    let reconnectTimer;
    let mounted = true;

    // 1) REST fetch for immediate price
    const fetchInitial = async () => {
      try {
        const res = await axios.get(
          'https://api.binance.com/api/v3/ticker/price',
          { params: { symbol: pair } }
        );
        if (!mounted) return;
        const p = parseFloat(res.data.price);
        lastPriceRef.current = p;
        setPrice(p);
      } catch (err) {
        console.error('Initial price fetch failed', err);
      }
    };

    // 2) WS connect + handlers
    const connect = () => {
      ws = new WebSocket(`wss://stream.binance.com:9443/ws/${streamName}`);

      ws.onopen = () => {
        // connection established
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          const newP = parseFloat(msg.p);
          if (lastPriceRef.current != null) {
            setPriceUp(newP >= lastPriceRef.current);
          }
          lastPriceRef.current = newP;
          setPrice(newP);
        } catch (e) {
          // ignore malformed
        }
      };

      // silently close & let onclose schedule reconnect
      ws.onerror = () => {
        ws.close();
      };

      ws.onclose = () => {
        if (!mounted) return;
        clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(connect, 5000);
      };
    };

    fetchInitial();
    connect();

    return () => {
      mounted = false;
      clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [pair, streamName]);

  // Add touch event handlers for swipe detection
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      if (!touchStartX.current || !touchEndX.current) return;

      const distance = touchEndX.current - touchStartX.current;
      
      // Check if swipe distance exceeds threshold
      if (Math.abs(distance) > SWIPE_THRESHOLD) {
        if (distance > 0) {
          // Swipe right
          if (onSwipe) onSwipe('right');
        } else {
          // Swipe left
          if (onSwipe) onSwipe('left');
        }
      }

      // Reset values
      touchStartX.current = null;
      touchEndX.current = null;
    };

    card.addEventListener('touchstart', handleTouchStart);
    card.addEventListener('touchmove', handleTouchMove);
    card.addEventListener('touchend', handleTouchEnd);

    return () => {
      card.removeEventListener('touchstart', handleTouchStart);
      card.removeEventListener('touchmove', handleTouchMove);
      card.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onSwipe]);

  // Modified to navigate immediately without waiting for API response
  const handleBet = (e) => {
    // Prevent event propagation to avoid triggering swipe detection
    e.stopPropagation();
    
    // Extract actual minutes from the horizon format
    const horizonMinutes = getHorizonMinutes(horizon);
    
    // Navigate to confirmation page immediately with market data
    navigate('/bet-confirmation', { 
      state: { 
        market,
        side: isUpForecast ? 'Up' : 'Down',
        currentPrice: price,
        // Include API endpoint details for the BetConfirmation page to fetch data
        predictionData: {
          symbol: asset + 'T',
          horizon: horizonMinutes,
          apiUrl: process.env.REACT_APP_AI_API_URL
        }
      }
    });
    
    // Also call original onBet function if needed
    if (onBet) {
      onBet(isUpForecast ? 'Up' : 'Down', market);
    }
  };  

  return (
    <div className="w-72" ref={cardRef}>
      <div className="animate-gradient-border p-0.5 rounded-3xl overflow-hidden">
      
        <Tilt
          tiltMaxAngleX={12}
          tiltMaxAngleY={12}
          glareEnable
          glareMaxOpacity={0.15}
          className="bg-gradient-to-br from-black/90 to-gray-900/90 p-5 rounded-3xl overflow-hidden"
          // Disable swipe behavior from the Tilt component if it has any
          tiltEnable={true}
          trackOnWindow={false}
          gyroscope={false}
        >
          <motion.div
            whileHover={{ scale: 1.03 }}
            transition={{ duration: 0.3 }}
            className="p-6 flex flex-col justify-between h-full rounded-3xl"
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-extrabold text-white">{asset}</h3>
                <p className="text-xs text-gray-400">
                  Horizon: <span className="text-white">{horizon}</span>
                </p>
              </div>
              <div
                className={`p-2 rounded-full ${
                  isUpForecast
                    ? 'bg-gradient-to-tr from-green-400 to-green-600'
                    : 'bg-gradient-to-tr from-red-400 to-red-600'
                }`}
              >
                {isUpForecast ? (
                  <ArrowUpIcon className="h-5 w-5 text-white" />
                ) : (
                  <ArrowDownIcon className="h-5 w-5 text-white" />
                )}
              </div>
            </div>

            {/* REAL‑TIME PRICE */}
            <div className="mb-4 h-24 flex items-center justify-center">
              {price != null ? (
                <span
                  className={`text-2xl font-bold ${
                    priceUp ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  $
                  {price.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              ) : (
                <div className="text-gray-500">Loading…</div>
              )}
            </div>

            {/* Confidence Bar */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-400">AI Confidence</span>
                <span className="text-xs font-semibold text-white">
                  {confidence}%
                </span>
              </div>
              <div className="w-full bg-gray-700 h-2 rounded-full overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${
                    isUpForecast ? 'bg-green-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${confidence}%` }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">
                Settles in <span className="text-white">{nextSettlement}</span>
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={handleBet}
                  className="bg-green-500 hover:bg-green-600 text-white font-semibold py-1 px-3 rounded-lg text-xs transition"
                >
                  Bet
                </button>
              </div>
            </div>
          </motion.div>
        </Tilt>
      </div>
    </div>
  );
}
